import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/local.js';
import type { LocalJournalPhoto } from '../../db/local.js';
import {
  addPhoto,
  localBlob,
  MAX_PHOTO_ATTEMPTS,
  photosOf,
  loadServerPhoto,
  removePhoto,
  resizeToJpeg,
  savePhotoToFiles,
} from '../../db/photos.js';

/**
 * Fotos eines Tagebuchtages.
 *
 * Jedes Bild sagt, wo es steht: noch auf dem Gerät, hochgeladen, oder — auf
 * einem zweiten Gerät — noch nicht angekommen. Ein leerer Rahmen ohne Erklärung
 * sähe aus wie ein kaputtes Bild.
 */
export function JournalPhotos({
  date,
  journalEntryId,
}: {
  date: string;
  journalEntryId: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const inputId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const photos = useLiveQuery(
    () => photosOf(journalEntryId, db),
    [journalEntryId],
    [] as LocalJournalPhoto[],
  );

  return (
    // Sprungziel für „Foto“ aus der Heute-Karte im Tagebuch.
    <div id="journal-day-photos" className="flex scroll-mt-24 flex-col gap-2">
      <span className="kw-label">{t('journal.photos.heading')}</span>

      {photos.length > 0 ? (
        <ul className="grid grid-cols-3 gap-2">
          {photos.map((photo) => (
            <PhotoThumb key={photo.id} photo={photo} date={date} />
          ))}
        </ul>
      ) : null}

      <label
        htmlFor={inputId}
        className={`kw-btn kw-btn-quiet kw-btn-small cursor-pointer ${busy ? 'opacity-50' : ''}`}
      >
        {busy ? t('journal.photos.preparing') : t('journal.photos.add')}
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="sr-only"
        disabled={busy}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          setBusy(true);
          setError(null);
          resizeToJpeg(file)
            .then((prepared) => addPhoto(date, prepared, db))
            .catch(() => setError(t('journal.photos.failed')))
            .finally(() => setBusy(false));
        }}
      />
      <p className="text-xs text-ink-muted">{t('journal.photos.hint')}</p>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function PhotoThumb({
  photo,
  date,
}: {
  photo: LocalJournalPhoto;
  date: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const blob = useLiveQuery(() => localBlob(photo.id, db), [photo.id], null);
  const [saved, setSaved] = useState(false);

  // Wo das Bild steht — ein leerer Rahmen ohne Erklärung sähe aus wie ein Fehler.
  const status = blob?.uploadedAt
    ? null
    : blob
      ? blob.attempts >= MAX_PHOTO_ATTEMPTS
        ? {
            text: t('journal.photos.uploadFailed', { reason: blob.lastError ?? '' }),
            tone: 'text-danger',
          }
        : { text: t('journal.photos.waiting'), tone: 'text-warn' }
      : photo.uploadedAt
        ? null
        : { text: t('journal.photos.elsewhere'), tone: 'text-ink-muted' };

  return (
    <li className="flex flex-col gap-1">
      <PhotoImage photo={photo} />
      {status ? <p className={`text-xs ${status.tone}`}>{status.text}</p> : null}
      {blob ? (
        <button
          type="button"
          onClick={() =>
            void savePhotoToFiles(blob.blob, `kraftwerk-${date}-${photo.id.slice(0, 8)}.jpg`).then(
              (outcome) => setSaved(outcome !== 'cancelled'),
            )
          }
          className="kw-btn kw-btn-quiet kw-btn-small px-2"
        >
          {saved ? t('journal.photos.saved') : t('journal.photos.saveToFiles')}
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => void removePhoto(photo.id, db)}
        className="kw-btn kw-btn-quiet kw-btn-small px-2 hover:border-danger hover:text-danger"
      >
        {t('journal.photos.remove')}
      </button>
    </li>
  );
}

/**
 * Das Bild selbst: vom Gerät, solange es dort liegt (auch offline), sonst vom
 * Server — oder ein Rahmen in der richtigen Form, wenn es noch nirgends hier ist.
 */
export function PhotoImage({ photo }: { photo: LocalJournalPhoto }): React.JSX.Element {
  const { t } = useTranslation();
  const blob = useLiveQuery(() => localBlob(photo.id, db), [photo.id], null);
  const [src, setSrc] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);

  // Vom Server über `fetch`, nicht als `<img src>`: Verschlüsselte Bilder (E21)
  // öffnet erst das Gerät.
  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    setLocked(false);
    const show = (next: Blob) => {
      url = URL.createObjectURL(next);
      setSrc(url);
    };
    if (blob) show(blob.blob);
    else if (photo.uploadedAt) {
      void loadServerPhoto(photo.id, db).then((loaded) => {
        if (!alive) return;
        if (loaded === 'locked') setLocked(true);
        else if (loaded) show(loaded);
      });
    } else setSrc(null);
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [blob, photo.id, photo.uploadedAt]);

  return (
    <div
      className="relative overflow-hidden rounded-lg border border-line bg-surface-raised"
      style={{ aspectRatio: `${photo.width} / ${photo.height}` }}
    >
      {src ? (
        <img
          src={src}
          alt={photo.caption ?? t('journal.photos.alt')}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      ) : locked ? (
        <p className="flex h-full items-center justify-center p-2 text-center text-xs text-ink-muted">
          {t('e2e.photoLocked')}
        </p>
      ) : null}
    </div>
  );
}
